/**
 * Lookahead scheduling ("A Tale of Two Clocks"): a coarse JS timer wakes up
 * every ~25 ms and schedules every musical step that starts within the next
 * ~120 ms on the sample-accurate AudioContext clock. The timer can jitter by
 * tens of milliseconds without the music ever wobbling.
 *
 * The musical grid is 4/4 in eighth-note steps (8 steps per bar). Step times
 * are computed from an anchor (never accumulated), so there is no drift.
 *
 * Pure and DOM-free: the caller passes the audio clock (unit-tested in node).
 */

/** How often the scheduler timer fires. */
export const SCHEDULER_INTERVAL_MS = 25;
/** How far ahead of the audio clock notes are scheduled. */
export const SCHEDULE_AHEAD_S = 0.12;
/**
 * If a step is this late (tab throttled, long GC pause, context resumed after
 * a stall) it is skipped rather than played late, so nothing ever bursts.
 */
export const MAX_LATENESS_S = 0.2;

export const STEPS_PER_BEAT = 2;
export const BEATS_PER_BAR = 4;
export const STEPS_PER_BAR = STEPS_PER_BEAT * BEATS_PER_BAR;

/** Gentle tempo range; theme bpm values outside it are clamped. */
export const BPM_RANGE = { min: 56, max: 132, fallback: 90 } as const;

export interface ScheduledStep {
  /** Absolute step index since the clock started (bar = floor(step / 8)). */
  step: number;
  /** AudioContext time (s) at which the step starts. */
  time: number;
}

export function clampBpm(bpm: number): number {
  if (!Number.isFinite(bpm)) return BPM_RANGE.fallback;
  return Math.min(BPM_RANGE.max, Math.max(BPM_RANGE.min, bpm));
}

/** Duration of one eighth-note step. */
export function secondsPerStep(bpm: number): number {
  return 60 / clampBpm(bpm) / STEPS_PER_BEAT;
}

export function secondsPerBar(bpm: number): number {
  return secondsPerStep(bpm) * STEPS_PER_BAR;
}

/** A step clock for one piece of music, anchored at `startTime` (step 0). */
export class StepClock {
  private readonly stepS: number;
  private next = 0;

  constructor(
    readonly startTime: number,
    bpm: number,
  ) {
    this.stepS = secondsPerStep(bpm);
  }

  get secondsPerStep(): number {
    return this.stepS;
  }

  get secondsPerBar(): number {
    return this.stepS * STEPS_PER_BAR;
  }

  /** Index of the next step that has not been handed out yet. */
  get nextStep(): number {
    return this.next;
  }

  timeOfStep(step: number): number {
    return this.startTime + step * this.stepS;
  }

  /**
   * Hand out every not-yet-scheduled step that starts before `until`, in
   * order. Steps more than MAX_LATENESS_S behind `now` are skipped (their
   * indices still advance, so bars stay aligned to the grid).
   */
  collect(now: number, until: number): ScheduledStep[] {
    const lateness = now - MAX_LATENESS_S - this.timeOfStep(this.next);
    if (lateness > 0) this.next += Math.ceil(lateness / this.stepS);
    const out: ScheduledStep[] = [];
    for (let t = this.timeOfStep(this.next); t < until; t = this.timeOfStep(this.next)) {
      out.push({ step: this.next, time: t });
      this.next++;
    }
    return out;
  }

  /**
   * The first bar boundary at or after time `t` that has not been scheduled
   * yet — where a theme change can start without cutting a bar in half.
   */
  nextBarAtOrAfter(t: number): ScheduledStep {
    const byTime = Math.ceil((t - this.startTime) / this.stepS - 1e-9);
    const first = Math.max(this.next, byTime);
    const step = Math.ceil(first / STEPS_PER_BAR) * STEPS_PER_BAR;
    return { step, time: this.timeOfStep(step) };
  }
}
